import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from './api';
import { useSwitchIfNeeded } from './useSwitchIfNeeded';

/**
 * Subscribes to the SSE stream at `/api/v1/notifications/stream`. Emits an event to the app-wide
 * TanStack Query cache when a new notification arrives — pages that display counts / feeds
 * automatically re-fetch.
 *
 * EventSource can't send Authorization headers, so we pass the token in the URL query. The API
 * server accepts it via a small middleware exception on this route (added in a later polish
 * pass — for MVP we rely on the fetch-Auth-header path via a polyfill; native EventSource works
 * once we ship the token-in-query auth reader).
 */

export interface StreamedNotification {
  id: string;
  userId: string;
  tenantId?: string;
  type: string;
  title: string;
  body: string;
  action?: { cta: string; url: string };
  at: string;
}

export function useNotificationStream(onEvent?: (n: StreamedNotification) => void) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const nav = useNavigate();
  const switchIfNeeded = useSwitchIfNeeded();

  /**
   * F-Step 104 — cross-workspace notification click.
   * If the notification belongs to a tenant other than the active workspace, switch first,
   * then navigate. Identity-level notifications (no tenantId) just navigate.
   */
  const onNotificationClick = useCallback(
    async (n: StreamedNotification) => {
      const target = n.action?.url;
      if (!target) return;
      try {
        await switchIfNeeded(n.tenantId ?? null);
      } catch {
        // Best-effort. If the target tenant isn't in memberships (revoked, deleted, etc.),
        // we still fall through to navigate — the destination page will 403 or redirect.
      }
      nav(target);
    },
    [switchIfNeeded, nav],
  );

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    const base = import.meta.env.VITE_API_BASE_URL || '/api/v1';
    const url = `${base}/notifications/stream`;

    // Modern browsers: EventSource works over cookies OR credentialed CORS. For MVP we rely on
    // same-origin + credentials: 'include' isn't available on EventSource, so we ship this as a
    // fetch-based line reader — works in every browser that ships ReadableStream.
    let closed = false;
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        setConnected(true);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() ?? '';
          for (const raw of events) {
            const lines = raw.split('\n');
            let evName = 'message';
            let data = '';
            for (const l of lines) {
              if (l.startsWith('event:')) evName = l.slice(6).trim();
              else if (l.startsWith('data:')) data += l.slice(5).trim();
            }
            if (evName === 'notification.new' && data) {
              try {
                const payload = JSON.parse(data) as StreamedNotification;
                onEvent?.(payload);
                qc.invalidateQueries({ queryKey: ['notifications'] });
                qc.invalidateQueries({ queryKey: ['offers'] });
                qc.invalidateQueries({ queryKey: ['applications'] });
              } catch { /* ignore malformed */ }
            }
          }
        }
      } catch {
        // ignore — reconnect handled by the effect re-running on remount
      } finally {
        setConnected(false);
      }
    })();

    return () => {
      closed = true;
      controller.abort();
    };
    // Intentionally omit onEvent from deps: caller passes a stable fn or accepts one call per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);

  return { connected, onNotificationClick };
}
